import {ActionMenu, ActionList, Spinner, Button, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {PlusIcon, TriangleDownIcon, type IconProps} from '@primer/octicons-react'
import {useSubAgents} from '../../hooks/useSubAgents'

import styles from './SubAgentSelector.module.css'
import {SubAgentsIcon16} from '../SubAgentsIcon'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {useMemo} from 'react'

type SubAgentSelectorProps = {
  repo: DropdownRepository
  branch: string
  selectedAgent: string | null
  onSelect: (agent: string | null) => void
  iconOnly?: boolean
  disabled?: boolean
}

export function SubAgentSelector({
  repo,
  branch,
  selectedAgent,
  onSelect,
  iconOnly = true,
  disabled = false,
}: SubAgentSelectorProps) {
  const {data: agents, isLoading} = useSubAgents({repo})

  const selectedAgentObj = agents?.find(agent => agent.name === selectedAgent)
  const selectedAgentName = selectedAgentObj?.display_name || selectedAgentObj?.name

  const newFileUrl = useMemo(() => {
    const params = new URLSearchParams({
      filename: '.github/agents/my-agent.md',
      value: '---\nname:\ndescription:\n---\n\n# My Agent\n\nDescribe what your agent does here...',
    })
    return `/${repo.owner}/${repo.nameOnly}/new/${branch}?${params.toString()}`
  }, [repo, branch])

  const triggerLabel = selectedAgentName || 'None'
  const ariaLabel = selectedAgentName
    ? `Select a custom agent, selected: ${selectedAgentName}`
    : 'Select a custom agent'

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {iconOnly ? (
          <IconButton
            className={clsx(styles.anchor, styles.iconButton)}
            icon={IconWithCheck}
            aria-label={ariaLabel}
            data-selected={Boolean(selectedAgentObj)}
            disabled={disabled}
          />
        ) : (
          <Button
            leadingVisual={SubAgentsIcon16}
            trailingAction={TriangleDownIcon}
            aria-label={ariaLabel}
            className={styles.anchor}
            data-selected={Boolean(selectedAgentObj)}
            disabled={disabled}
          >
            {triggerLabel}
          </Button>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        <ActionList>
          {isLoading ? (
            <>
              <ActionList.Item>
                <ActionList.LeadingVisual>
                  <Spinner size="small" />
                </ActionList.LeadingVisual>
                Loading
              </ActionList.Item>
              <ActionList.Divider />
            </>
          ) : (
            agents &&
            agents.length > 0 && (
              <>
                <ActionList.Group selectionVariant="single">
                  <ActionList.Item selected={selectedAgent === null} onSelect={() => onSelect(null)}>
                    None
                  </ActionList.Item>
                  {agents.map(agent => (
                    <ActionList.Item
                      key={agent.name}
                      selected={selectedAgent === agent.name}
                      onSelect={() => onSelect(agent.name)}
                    >
                      {agent.display_name || agent.name}
                    </ActionList.Item>
                  ))}
                </ActionList.Group>
                <ActionList.Divider />
              </>
            )
          )}

          <ActionList.Group selectionVariant={false}>
            <ActionList.LinkItem href={newFileUrl} target="_blank">
              <ActionList.LeadingVisual>
                <PlusIcon />
              </ActionList.LeadingVisual>
              Create an agent
            </ActionList.LinkItem>
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

const IconWithCheck = (props: IconProps) => {
  return (
    <>
      <SubAgentsIcon16 {...props} />
      <CheckIcon />
    </>
  )
}

const CheckIcon = () => {
  return (
    <svg
      className={styles.checkIcon}
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M7.41406 1.63379C7.75703 1.66786 8.08279 1.80212 8.34961 2.02051L8.47754 2.13672L8.47852 2.1377C8.806 2.46569 8.99015 2.91038 8.99023 3.37402C8.99023 3.7798 8.84934 4.17121 8.59473 4.48242L8.47852 4.61133L5.22754 7.8623C4.89954 8.18968 4.45486 8.37398 3.99121 8.37402C3.52761 8.37402 3.08292 8.18965 2.75488 7.8623L1.49121 6.59863L1.47949 6.58594C1.17042 6.25422 1.00181 5.8156 1.00977 5.3623L1.02148 5.19336C1.06638 4.80245 1.2419 4.43583 1.52246 4.15527C1.84299 3.83487 2.27541 3.65165 2.72852 3.64355L2.89844 3.64844C3.23454 3.67514 3.55557 3.79897 3.82324 4.00391L3.95312 4.1123L3.99121 4.15039L6.00391 2.1377V2.13672C6.33205 1.80893 6.77743 1.625 7.24121 1.625L7.41406 1.63379Z"
        fill="#0969DA"
      />
      <path
        d="M7.41406 1.63379C7.75703 1.66786 8.08279 1.80212 8.34961 2.02051L8.47754 2.13672L8.47852 2.1377C8.806 2.46569 8.99015 2.91038 8.99023 3.37402C8.99023 3.7798 8.84934 4.17121 8.59473 4.48242L8.47852 4.61133L5.22754 7.8623C4.89954 8.18968 4.45486 8.37398 3.99121 8.37402C3.52761 8.37402 3.08292 8.18965 2.75488 7.8623L1.49121 6.59863L1.47949 6.58594C1.17042 6.25422 1.00181 5.8156 1.00977 5.3623L1.02148 5.19336C1.06638 4.80245 1.2419 4.43583 1.52246 4.15527C1.84299 3.83487 2.27541 3.65165 2.72852 3.64355L2.89844 3.64844C3.23454 3.67514 3.55557 3.79897 3.82324 4.00391L3.95312 4.1123L3.99121 4.15039L6.00391 2.1377V2.13672C6.33205 1.80893 6.77743 1.625 7.24121 1.625L7.41406 1.63379Z"
        stroke="white"
        strokeWidth="2"
      />
      <path
        d="M7.41406 1.63379C7.75703 1.66786 8.08279 1.80212 8.34961 2.02051L8.47754 2.13672L8.47852 2.1377C8.806 2.46569 8.99015 2.91038 8.99023 3.37402C8.99023 3.7798 8.84934 4.17121 8.59473 4.48242L8.47852 4.61133L5.22754 7.8623C4.89954 8.18968 4.45486 8.37398 3.99121 8.37402C3.52761 8.37402 3.08292 8.18965 2.75488 7.8623L1.49121 6.59863L1.47949 6.58594C1.17042 6.25422 1.00181 5.8156 1.00977 5.3623L1.02148 5.19336C1.06638 4.80245 1.2419 4.43583 1.52246 4.15527C1.84299 3.83487 2.27541 3.65165 2.72852 3.64355L2.89844 3.64844C3.23454 3.67514 3.55557 3.79897 3.82324 4.00391L3.95312 4.1123L3.99121 4.15039L6.00391 2.1377V2.13672C6.33205 1.80893 6.77743 1.625 7.24121 1.625L7.41406 1.63379Z"
        stroke="#DDF4FF"
        strokeWidth="2"
      />
    </svg>
  )
}

try{ SubAgentSelector.displayName ||= 'SubAgentSelector' } catch {}
try{ IconWithCheck.displayName ||= 'IconWithCheck' } catch {}
try{ CheckIcon.displayName ||= 'CheckIcon' } catch {}